import { LitElement, html, css, unsafeCSS, property } from "lit-element";

import { reportsApi } from "../../api/reportsApi";
import { seasonsApi } from "../../api/seasonsApi";
import { seriesApi } from "../../api/seriesApi";

import { DropdownModel } from "../../models/components/DropdownModel";
import { SeriesModel } from "../../models/media/SeriesModel";
import { SeasonModel } from "../../models/media/SeasonModel";
import { TemplateModel } from "../../models/template/TemplateModel";
import { ReportFilterModel } from "../../models/report/ReportFilterModel";

import ReportType from "../../enums/report-type";

import Dropdown from "../dropdown/dropdown";

import defaultReportConfigStyles from "./default-report-config.scss";
import { UserModel } from "../../models/dashboard/UserModel";
import { DefaultReportConfigItemModel, DefaultReportConfigModel } from "../../models/report/DefaultReportConfigModel";

export default class DefaultReportConfigItem extends LitElement {
    @property({ type: Object })
    reportTemplates: TemplateModel[];

    @property({ type: Object })
    selectedReportTemplate: TemplateModel;

    @property({ type: Object })
    selectedUser: UserModel;

    @property({ type: Number })
    selectedReportType: number;

    private _loadingContainer: HTMLElement;

    private _mediaUrl = "";
    private _reportsUrl = "";
    private _userId = "";

    private _users: UserModel[]
    private _mediaReportsApi: reportsApi;
    private _reportsApi: reportsApi;

    private _defaultConfig: DefaultReportConfigModel;
    private _defaultConfigItem: DefaultReportConfigItemModel;
    private _reportType: number;

    private _requestUpdateFunc: any;


    constructor(
        loadingContainer: HTMLElement,
        defaultConfigItem: DefaultReportConfigItemModel,
        mediaUrl: string,
        reportsUrl: string,
        userId: string,
        users: UserModel[],
        reportType: number,
        defaultConfig: DefaultReportConfigModel,
        requestUpdateFunc: any) {
        super();

        this._loadingContainer = loadingContainer;

        if (defaultConfigItem) {
            this._defaultConfigItem = defaultConfigItem;
        }
        else {
            this._defaultConfigItem = <DefaultReportConfigItemModel>{
                reportId: '',
                userId: this._userId,
                templateId: '',
                reportType: this._reportType ?? 0
            }
        }

        this._defaultConfig = defaultConfig;
        this._requestUpdateFunc = requestUpdateFunc;
        this._reportType = reportType;
        this._mediaUrl = mediaUrl;
        this._reportsUrl = reportsUrl;
        this._userId = userId;
        this._users = users.sort((a, b) => this.textSort(a, b, "userName"));;

        this._mediaReportsApi = new reportsApi(this._mediaUrl, this._userId);
        this._reportsApi = new reportsApi(this._reportsUrl, this._userId);

        this.reportTemplates = [];
    }

    async firstUpdated() {

        this.showLoading(true);

        if (this._users) {

            if (this._defaultConfigItem && this._defaultConfigItem.userId)
                this.selectedUser = this._users.find(x => x.userGuid === this._defaultConfigItem.userId);
            else if (this._users.length === 1)
                this.selectedUser = this._users[0];

            if (this._defaultConfigItem && this._defaultConfigItem.reportType)
                this.selectedReportType = this._defaultConfigItem.reportType;
        }

        let matchTemplates: TemplateModel[] = [];
        let teamMatchTemplates: TemplateModel[] = [];
        let playerMatchTemplates: TemplateModel[] = [];

        let apiCalls = [
            this._mediaReportsApi.GetTemplatesByReportTypeFromMedia(this.selectedUser?.userGuid ?? this._userId, ReportType.MatchReport),
            this._mediaReportsApi.GetTemplatesByReportTypeFromMedia(this.selectedUser?.userGuid ?? this._userId, ReportType.TeamMatchReport),
            this._mediaReportsApi.GetTemplatesByReportTypeFromMedia(this.selectedUser?.userGuid ?? this._userId, ReportType.PlayerMatchReport),
        ];

        [matchTemplates, teamMatchTemplates, playerMatchTemplates] = await Promise.all(apiCalls)

        if (matchTemplates) {
            this.reportTemplates = this.reportTemplates.concat(matchTemplates);
        }
        if (teamMatchTemplates) {
            this.reportTemplates = this.reportTemplates.concat(teamMatchTemplates);
        }
        if (playerMatchTemplates) {
            this.reportTemplates = this.reportTemplates.concat(playerMatchTemplates);
        }

        if (this._defaultConfigItem && this._defaultConfigItem.templateId) {
            this.selectedReportTemplate = this.reportTemplates.find(x => x.reportTemplateId == this._defaultConfigItem.templateId);
        }

        this.reportTemplates.sort((a, b) => this.textSort(a, b, "name"));

        this.showLoading(false);
    }

    isEmpty(obj) {
        return Object.keys(obj).length === 0;
    }

    textSort(a, b, property) {
        let returnValue = 0;

        if (a[property] < b[property])
            returnValue = -1;
        else
            returnValue = 1;

        return returnValue;
    }

    getUsers() {
        let dropdownItems: DropdownModel[] = [];

        dropdownItems.push(new DropdownModel(undefined, "-- Users --", null));
        for (let [i, user] of Object.entries(this._users)) {
            dropdownItems.push(new DropdownModel(user.userGuid, user.userName, user));
        }

        let selectedValue = this.selectedUser ? this.selectedUser.userGuid : undefined;

        return new Dropdown(dropdownItems, this.userChange, selectedValue, "User");
    }

    getTemplates() {
        let dropdownItems: DropdownModel[] = [];

        dropdownItems.push(new DropdownModel(undefined, "-- Reports --", null));
        for (let [i, reportTemplate] of Object.entries(this.reportTemplates)) {
            dropdownItems.push(new DropdownModel(reportTemplate.reportTemplateId, reportTemplate.name, reportTemplate));
        }

        let selectedValue = this.selectedReportTemplate ? this.selectedReportTemplate.reportTemplateId : undefined;

        return new Dropdown(dropdownItems, this.reportTemplateChange, selectedValue, "Report");
    }

    buildReportTypeDropdown() {
        let dropdownItems: DropdownModel[] = [];

        dropdownItems.push(new DropdownModel(undefined, "Please select a Report Type", null));
        dropdownItems.push(new DropdownModel(ReportType.TeamMatchReport, "Team Match Report", this._defaultConfigItem));
        dropdownItems.push(new DropdownModel(ReportType.PlayerMatchReport, "Player Match Report", this._defaultConfigItem));
        dropdownItems.push(new DropdownModel(ReportType.TeamSeasonReport, "Team Season Report", this._defaultConfigItem));
        dropdownItems.push(new DropdownModel(ReportType.PlayerSeasonReport, "Player Season Report", this._defaultConfigItem));
        dropdownItems.push(new DropdownModel(ReportType.TeamSeriesReport, "Team Series Report", this._defaultConfigItem));
        dropdownItems.push(new DropdownModel(ReportType.PlayerSeriesReport, "Player Series Report", this._defaultConfigItem));
        dropdownItems.push(new DropdownModel(ReportType.MatchReport, "Match Report", this._defaultConfigItem));
        dropdownItems.push(new DropdownModel(ReportType.SeasonReport, "Season Report", this._defaultConfigItem));
        dropdownItems.push(new DropdownModel(ReportType.ViewMatchReport, "View Match Dashboard", this._defaultConfigItem));

        return new Dropdown(dropdownItems, this.reportTypeChange, this.selectedReportType ? this.selectedReportType.toString() : null, 'Report Type');
    }

    reportTypeChange(value, configItem: DefaultReportConfigItemModel) {
        this.selectedReportType = parseInt(value);
        configItem.reportType = parseInt(value);
    }

    userChange = async (value: string, userModel: UserModel) => {
        this.selectedUser = userModel;

        this._defaultConfigItem.userId = this.selectedUser.userGuid

        this.reportTemplates = [];
        this.selectedReportTemplate = undefined;

        if (!userModel)
            return;

        this.showLoading(true);

        // Repopulate on User Select
        let apiCalls = [
            this._mediaReportsApi.GetTemplatesByReportTypeFromMedia(this.selectedUser?.userGuid ?? this._userId, ReportType.MatchReport),
            this._mediaReportsApi.GetTemplatesByReportTypeFromMedia(this.selectedUser?.userGuid ?? this._userId, ReportType.TeamMatchReport),
            this._mediaReportsApi.GetTemplatesByReportTypeFromMedia(this.selectedUser?.userGuid ?? this._userId, ReportType.PlayerMatchReport),
        ];

        let seasonTemplates: TemplateModel[] = [];
        let teamSeasonTemplates: TemplateModel[] = [];
        let playerSeasonTemplates: TemplateModel[] = [];

        [seasonTemplates, teamSeasonTemplates, playerSeasonTemplates] = await Promise.all(apiCalls);

        if (seasonTemplates) {
            this.reportTemplates = this.reportTemplates.concat(seasonTemplates);
        }
        if (teamSeasonTemplates) {
            this.reportTemplates = this.reportTemplates.concat(teamSeasonTemplates);
        }
        if (playerSeasonTemplates) {
            this.reportTemplates = this.reportTemplates.concat(playerSeasonTemplates);
        }

        this.reportTemplates.sort((a, b) => this.textSort(a, b, "name"));

        this.showLoading(false);
    }

    reportTemplateChange = (value: string, reportTemplate: TemplateModel) => {
        this.selectedReportTemplate = reportTemplate;
        this._defaultConfigItem.templateId = reportTemplate.reportTemplateId;
    }

    deleteConfigItem = async () => {
        this._defaultConfig.configItems = this._defaultConfig.configItems.filter(x => x !== this._defaultConfigItem);

        await this._reportsApi.PostDefaultReportConfigFromReports(this._userId, this._defaultConfig);

        this._requestUpdateFunc();
    }

    saveConfig = async () => {

        let thisConfigItem = this._defaultConfig.configItems.find(x => x === this._defaultConfigItem);

        if (!thisConfigItem) {

            if (this._reportType) {
                this._defaultConfigItem.reportType = this._reportType;
            }

            this._defaultConfig.configItems.push(this._defaultConfigItem);
        }

        await this._reportsApi.PostDefaultReportConfigFromReports(this._userId, this._defaultConfig);
        this._requestUpdateFunc();
    }

    showLoading(isLoading: boolean) {
        if (isLoading) {
            this._loadingContainer.innerHTML = "Loading...";
        }
        else {
            this._loadingContainer.innerHTML = "";
        }
    }

    createRenderRoot() {
        return this;
    }

    render() {

        return [
            html`
            <style>
                ${css`${unsafeCSS(defaultReportConfigStyles)}`}                     
            </style>

            <div class="container">                

                <div class="row">  
            
                    ${(this._users.length > 0) ? html`<div class="col"> ${this.getUsers()} </div>` : html`<div class="col"></div>`}

                    ${(this.reportTemplates.length > 0) ? html`<div class="col"> ${this.getTemplates()} </div>` : html`<div class="col"></div>`}

                    ${this._reportType ? html``
                    : html`<div class="col"> ${this.buildReportTypeDropdown()} </div>`}

                    ${(this.reportTemplates) && html`<div class="col default-config-btn-col" style="${this._reportType ? 'margin-bottom:0px !important' : 'margin-bottom:0px !important'}"> <button class="btn btn-danger wide-element" @click=${() => this.deleteConfigItem()}>Clear</button> </div>`}
                    
                    ${this._reportType ? html`<div class="col default-config-btn-col" style="margin-bottom:0px !important"> <button class="btn btn-info wide-element" @click=${() => this.saveConfig()}>Save</button> </div>` : html``}
                </div>

            </div>`
        ];
    }
}

window.customElements.define('stratus-stats-default-report-config-item', DefaultReportConfigItem as any);